"use client";

import { IconButton, IconSizes } from "@brexhq/component-library";
import { useBreakpoint } from "@brexhq/metal-utils";
import styled from "@emotion/styled";
import { getCookies, setCookie } from "cookies-next";
import React, { useEffect, useState, useMemo, FC, useCallback } from "react";

import { MarketoForm } from "@brexhq/next/src/components/building-blocks/MarketoForm/MarketoForm";
import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import BlockRenderer from "@brexhq/next/src/components/shared/BlockRenderer";
import { BynderImage } from "@brexhq/next/src/components/shared/BynderAsset/Image/BynderImage";
import { Link } from "@brexhq/next/src/components/shared/Link";
import { extractTextContent } from "@brexhq/next/src/utils/extractTextContent";
import { TitleM } from "@brexhq/shared/components/Typography/Components";
import { Text } from "@brexhq/shared/components/Typography/Text";
import breakpoints from "@brexhq/shared/styles/breakpoints";

import { OverlayPopupData, OverlayPopupProps } from "./types";

const Overlay = styled.div<{ overlayColor: string }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ overlayColor }) => overlayColor};
  z-index: 1000;
  transition: opacity 0.3s ease-out;
  backdrop-filter: blur(8px);
`;

const Wrapper = styled.div(({ theme }) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",

  [theme.breakpoints.SML]: {
    width: "100%",
    maxWidth: "100%",
    left: 0,
    top: "unset",
    bottom: 0,
    transform: "translate(0, 0)",
  },
}));

const ContentContainer = styled.div<{ withImage?: boolean | null }>(
  ({ withImage, theme }) => ({
    position: "relative",
    borderRadius: theme.radius.cardRadius,
    maxWidth: withImage ? "1000px" : "500px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    background: withImage ? "white" : "transparent",
    overflow: "hidden",
  }),
);

const ColumnsLayoutRowContainer = styled.div(({ theme }) => ({
  display: "flex",
  width: "100%",
  [theme.breakpoints.SML]: {
    flexDirection: "column",
  },
}));

const FormContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.LRG.xxsml}px;
`;

const ImageContainer = styled.div(({ theme }) => ({
  flex: 1,
  position: "relative",
  "& span": {
    minHeight: "100%",
  },
  "& div": {
    height: "100%",
  },
  [theme.breakpoints.SML]: {
    maxHeight: "50vh",
  },
}));

const Image = styled(BynderImage)`
  object-fit: cover;
  image-rendering: high-quality;
  image-resolution: from-image;
  object-position: 50% 50%;
`;

const FORM_SUCCESS_DISPLAY_DURATION = 1500;

const DARK_OVERLAY_COLOR = "rgba(0, 0, 0, 0.75)";
const LIGHT_OVERLAY_COLOR = "rgba(255, 255, 255, 0.75)";

const DEFAULT_SCROLL_PERCENTAGE = "0";

const CloseButton = styled(IconButton)`
  ${({ theme }) => ({
    position: "absolute",
    top: `${theme.spacing.LRG.xxsml}px`,
    right: `${theme.spacing.LRG.xxsml}px`,
    zIndex: theme.zIndex.stickyHeader,
  })}
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.LRG.xsml}px;
  padding: ${({ theme }) => theme.spacing.LRG.xsml}px;
`;

const TitleDescriptionLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.LRG.xsml}px;
  padding: ${({ theme }) => theme.spacing.LRG.sml}px;
`;

const getContentBlocks = ({
  data,
  isMobile,
  onFormSuccess,
}: {
  data: OverlayPopupData;
  isMobile: boolean;
  onFormSuccess: () => void;
}) => {
  const { standard } = getModuleThemeColors("PrimaryWhite");

  const description = extractTextContent(data, "description");
  const getContent = (data: OverlayPopupData) => {
    if (!data.title && !description && !data.link) {
      return null;
    }

    return (
      <TitleDescriptionLinkWrapper>
        {data.title && <TitleM>{data.title}</TitleM>}
        {description && (
          <BlockRenderer
            blocks={description}
            theme={standard}
            typeScale="M"
            component="overlay-popup"
          />
        )}
        {data.link && data.link.label && data.link.to && (
          <Link
            modifier="primary"
            to={data.link.to}
            label={data.link.label}
            component="overlay-popup"
            margin={[0, 0, 0, 0]}
          />
        )}
      </TitleDescriptionLinkWrapper>
    );
  };

  const getImageContent = (data: OverlayPopupData, isMobile: boolean) => {
    if (!data.columnsLayout || (!data.imageDesktop && !data.imageMobile)) {
      return null;
    }

    const imageToUse =
      isMobile && data.imageMobile ? data.imageMobile : data.imageDesktop;

    if (!imageToUse) return null;

    return (
      <ImageContainer>
        <Image image={imageToUse} loading="eager" maxWidth={2500} />
      </ImageContainer>
    );
  };

  const FORM_BLOCK =
    data.form && !data.link ? (
      <FormContainer>
        <Container flex={1} maxWidth="500px">
          <MarketoForm
            {...data.form}
            FormHeaderComponent={
              data?.form?.formHeader ? (
                <Text as="h2">{data.form.formHeader}</Text>
              ) : null
            }
            successCallback={onFormSuccess}
          />
        </Container>
      </FormContainer>
    ) : null;

  const TITLE_DESCRIPTION_LINK_BLOCK = getContent(data);
  const currentImagePosition = isMobile
    ? data.imagePositionMobile
    : data.imagePositionDesktop;

  const CONTENT_BLOCK = (
    <ContentWrapper>
      {TITLE_DESCRIPTION_LINK_BLOCK}
      {FORM_BLOCK}
    </ContentWrapper>
  );

  if (!data.columnsLayout || !currentImagePosition) {
    return CONTENT_BLOCK;
  }

  const IMAGE_BLOCK = getImageContent(data, isMobile);

  if (!currentImagePosition) {
    return CONTENT_BLOCK;
  }

  const LAYOUT_BLOCKS = {
    left: [IMAGE_BLOCK, CONTENT_BLOCK],
    right: [CONTENT_BLOCK, IMAGE_BLOCK],
    top: [IMAGE_BLOCK, CONTENT_BLOCK],
    bottom: [CONTENT_BLOCK, IMAGE_BLOCK],
    none: [CONTENT_BLOCK],
  };

  const blocks = LAYOUT_BLOCKS[currentImagePosition];

  if (currentImagePosition === "left" || currentImagePosition === "right") {
    return <ColumnsLayoutRowContainer>{blocks}</ColumnsLayoutRowContainer>;
  }

  return blocks;
};

export const OverlayPopup: FC<OverlayPopupProps> = ({ data }) => {
  const [render, setRender] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const isMobile = useBreakpoint(breakpoints.SML);

  const handleScroll = useCallback(() => {
    const scrollPercentage =
      (window.scrollY /
        (document.documentElement.scrollHeight - window.innerHeight)) *
      100;
    const targetPercentage = parseFloat(
      data.scrollPercentage ?? DEFAULT_SCROLL_PERCENTAGE,
    );

    if (scrollPercentage >= targetPercentage && !hasScrolled) {
      setHasScrolled(true);
    }
  }, [data.scrollPercentage, hasScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const cookieStore = getCookies();
    const showCookie = cookieStore.show_overlay_popup;

    if (!showCookie && hasScrolled) {
      setCookie("show_overlay_popup", "true");
      setRender(true);
    }

    if (showCookie === "false") {
      setRender(false);
    }

    if (showCookie === "true" && hasScrolled) {
      setRender(true);
    }
  }, [hasScrolled]);

  useEffect(() => {
    if (!submitted) {
      return;
    }

    const timeout = setTimeout(() => {
      setRender(false);
    }, FORM_SUCCESS_DISPLAY_DURATION);

    return () => clearTimeout(timeout);
  }, [submitted]);

  const handleClose = () => {
    setCookie("show_overlay_popup", "false");
    setRender(false);
  };

  const handleFormSuccess = () => {
    setCookie("show_overlay_popup", "false");
    setSubmitted(true);
  };

  const renderContent = useMemo(
    () =>
      getContentBlocks({
        data,
        isMobile,
        onFormSuccess: handleFormSuccess,
      }),
    [isMobile, data],
  );

  return render ? (
    <Overlay
      overlayColor={
        data.overlayColor === "light" ? LIGHT_OVERLAY_COLOR : DARK_OVERLAY_COLOR
      }
    >
      <Wrapper>
        <ContentContainer
          withImage={
            data.columnsLayout &&
            (isMobile
              ? !!data.imageMobile && data.imagePositionMobile !== "none"
              : !!data.imageDesktop)
          }
        >
          {data.showCloseButton && (
            <CloseButton
              icon="close"
              onClick={handleClose}
              size={IconSizes.LARGE}
              aria-label="Close popup"
            />
          )}
          {renderContent}
        </ContentContainer>
      </Wrapper>
    </Overlay>
  ) : null;
};
